import { Table } from "@xola/ui-kit";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Merchandise, MerchandiseCatalogItem } from "types";
import { EditVariantModal } from "./EditVariantModal";
import { useVariantBulkActionModals } from "./useVariantBulkActionModals";
import { useVariantCatalogItems } from "./useVariantCatalogItems";
import { VariantActionsHeader } from "./VariantActionsHeader";
import { VariantInventoryRow } from "./VariantInventoryRow";
import { getVariantPath, VariantCatalogItem } from "./VariantTableHelper";
import { ProductImage } from "@/components/Common/ProductImage";
import { isInventoryTracked } from "@/models/Merchandise";

interface VariantGroupItemInventoryTableProps {
    merchandise: Merchandise;
    catalogItems?: MerchandiseCatalogItem[];
}

export interface VariantGroupItemInventoryTableHandle {
    saveChangedCatalogItems: () => Promise<void>;
}

export const VariantGroupItemInventoryTable = forwardRef<
    VariantGroupItemInventoryTableHandle,
    VariantGroupItemInventoryTableProps
>(({ merchandise, catalogItems: fetchedCatalogItems }, ref) => {
    const [editingItem, setEditingItem] = useState<VariantCatalogItem | null>(null);
    const isTracked = isInventoryTracked(merchandise);

    const { table, selection, bulkActions, saveChangedCatalogItems, handleSaveItem } = useVariantCatalogItems(
        merchandise,
        fetchedCatalogItems,
    );

    const { handleActionClick, modals } = useVariantBulkActionModals({
        selectedCount: selection.items.size,
        onUpdateStatus: bulkActions.updateSelectedStatus,
        onUpdatePrice: bulkActions.updateSelectedPrice,
        onUpdateOversellPolicy: bulkActions.updateSelectedOversellPolicy,
        onUpdateLowInventory: bulkActions.updateSelectedLowInventory,
    });

    useImperativeHandle(ref, () => ({ saveChangedCatalogItems }), [saveChangedCatalogItems]);

    return (
        <div className="space-y-4">
            <div className="rounded-lg">
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header className="w-2/5">Name</Table.Header>
                            <Table.Header>SKU</Table.Header>
                            <Table.Header>Barcode</Table.Header>
                            <Table.Header>Price</Table.Header>
                            {isTracked && <Table.Header>Inventory</Table.Header>}
                            <Table.Header className="w-24">
                                <VariantActionsHeader
                                    selectedCount={selection.items.size}
                                    isAllSelected={selection.isAllSelected}
                                    isIndeterminate={selection.isIndeterminate}
                                    merchandise={merchandise}
                                    onActionClick={handleActionClick}
                                    onToggleSelectAll={selection.toggleSelectAll}
                                />
                            </Table.Header>
                        </Table.Row>
                    </Table.Head>
                    <Table.Body isStriped={false}>
                        <Table.Row className="border-b border-gray-light">
                            <Table.Cell>
                                <div className="flex items-center space-x-3">
                                    <ProductImage product={merchandise} size="small" />
                                    <span className="whitespace-pre-wrap break-all font-bold">{merchandise.name}</span>
                                </div>
                            </Table.Cell>
                            <Table.Cell />
                            <Table.Cell />
                            <Table.Cell />
                            {isTracked && <Table.Cell />}
                            <Table.Cell />
                        </Table.Row>

                        {table.rootItems.map((item) => (
                            <VariantInventoryRow
                                key={item.id}
                                item={item}
                                merchandise={merchandise}
                                tree={table.tree}
                                expandedItems={table.expandedItems}
                                setExpandedItems={table.setExpandedItems}
                                selectedItems={selection.items}
                                toggleSelectItem={selection.toggleSelectItem}
                                toggleGroupItems={selection.toggleGroupItems}
                                setEditingItem={setEditingItem}
                                duplicateSkuItemIds={table.duplicateSkuItemIds}
                                isInventoryTracked={isTracked}
                            />
                        ))}
                    </Table.Body>
                </Table>
            </div>

            {editingItem?.id && (
                <EditVariantModal
                    isOpen
                    catalogItemId={editingItem.id}
                    merchandise={merchandise}
                    publicVariantCount={table.publicVariantCount}
                    headerTitle="Edit Item"
                    variantPath={getVariantPath(editingItem, table.catalogItemsWithDraftValues)}
                    onClose={() => setEditingItem(null)}
                    onSavedCatalogItem={(savedCatalogItem) =>
                        handleSaveItem({
                            ...editingItem,
                            ...savedCatalogItem,
                        })
                    }
                />
            )}

            {modals}
        </div>
    );
});
